import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './SafetyGuidelines.css';

const SafetyGuidelines = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('safety');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="safety-guidelines-container">
      <div className="safety-guidelines-header">
        <div className="safety-guidelines-header-content">
          <h1>{t('header.title', 'Safety Guidelines')}</h1>
          <p className="safety-guidelines-subtitle">
            {t('header.subtitle', 'Your safety is our top priority. These guidelines help ensure a safe and positive experience for all BrauksimKopā users.')}
          </p>
          <p className="safety-guidelines-date">{t('header.lastUpdated', 'Last updated: January 2025')}</p>
        </div>
      </div>

      <div className="safety-guidelines-content">
        <nav className="safety-guidelines-nav">
          <h3>{t('navigation.title', 'Quick Navigation')}</h3>
          <ul>
            <li><a href="#overview">{t('navigation.overview', 'Safety Overview')}</a></li>
            <li><a href="#before-meeting">{t('navigation.beforeMeeting', 'Before Meeting')}</a></li>
            <li><a href="#meeting-safely">{t('navigation.meetingSafely', 'Meeting Safely')}</a></li>
            <li><a href="#during-rides">{t('navigation.duringRides', 'During Rides')}</a></li>
            <li><a href="#package-safety">{t('navigation.packageSafety', 'Package Safety')}</a></li>
            <li><a href="#personal-safety">{t('navigation.personalSafety', 'Personal Safety')}</a></li>
            <li><a href="#vehicle-safety">{t('navigation.vehicleSafety', 'Vehicle Safety')}</a></li>
            <li><a href="#communication">{t('navigation.communication', 'Safe Communication')}</a></li>
            <li><a href="#red-flags">{t('navigation.redFlags', 'Warning Signs')}</a></li>
            <li><a href="#emergency">{t('navigation.emergency', 'Emergency Procedures')}</a></li>
            <li><a href="#reporting">{t('navigation.reporting', 'Reporting Issues')}</a></li>
            <li><a href="#prevention">{t('navigation.prevention', 'Prevention Tips')}</a></li>
          </ul>
        </nav>

        <div className="safety-guidelines-main">
          <section id="overview">
            <h2>{t('overview.title', '1. Safety Overview')}</h2>
            
            <div className="safety-priority-box">
              <h4>{t('overview.safetyFirst.title', '🛡️ Safety First Philosophy')}</h4>
              <p>{t('overview.safetyFirst.description', 'At BrauksimKopā, safety is not just a feature—it\'s our fundamental principle. Every decision we make is guided by our commitment to protecting our users and creating a trustworthy community.')}</p>
            </div>

            <h3>{t('overview.sharedResponsibility.title', '1.1 Shared Responsibility')}</h3>
            <p>{t('overview.sharedResponsibility.description', 'Safety on our platform is a shared responsibility between BrauksimKopā and our users:')}</p>
            <ul>
              <li><strong>{t('overview.sharedResponsibility.ourCommitment', 'Our Commitment: We provide safety tools, verification systems, and support')}</strong></li>
              <li><strong>{t('overview.sharedResponsibility.yourResponsibility', 'Your Responsibility: Use these tools wisely and follow safety guidelines')}</strong></li>
              <li><strong>{t('overview.sharedResponsibility.communityEffort', 'Community Effort: Together we create a safer environment for everyone')}</strong></li>
            </ul>

            <h3>{t('overview.safetyFeatures.title', '1.2 Safety Features')}</h3>
            <p>{t('overview.safetyFeatures.description', 'BrauksimKopā includes several safety features:')}</p>
            <ul>
              {(() => {
                const list = t('overview.safetyFeatures.list', { returnObjects: true });
                const defaultList = [
                  'User verification and profile validation',
                  'Rating and review system',
                  'In-app messaging (no need to share personal numbers)',
                  'Trip and delivery tracking',
                  'Reporting and blocking functionality',
                  'Trust scoring algorithms',
                  '24/7 safety support'
                ];
                const safetyFeatures = Array.isArray(list) ? list : defaultList;
                return safetyFeatures.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>
          </section>

          <section id="before-meeting">
            <h2>{t('beforeMeeting.title', '2. Before Meeting Other Users')}</h2>
            
            <h3>{t('beforeMeeting.profileVerification.title', '2.1 Profile Verification')}</h3>
            <p>{t('beforeMeeting.profileVerification.description', 'Always check user profiles before agreeing to rides or deliveries:')}</p>
            <ul>
              {(() => {
                const list = t('beforeMeeting.profileVerification.list', { returnObjects: true });
                const defaultList = [
                  'Review Ratings: Look at overall ratings and read recent reviews',
                  'Check Profile Completeness: Complete profiles indicate serious users',
                  'Vehicle Information: For drivers, verify vehicle details match',
                  'Verification Badges: Look for verified phone numbers and email addresses',
                  'Trust Your Instincts: If something feels off, choose another user'
                ];
                const items = Array.isArray(list) ? list : defaultList;
                return items.map((item, index) => (
                  <li key={index}><strong>{item}</strong></li>
                ));
              })()}
            </ul>

            <h3>{t('beforeMeeting.communication.title', '2.2 Communication Guidelines')}</h3>
            <p>{t('beforeMeeting.communication.description', 'Use our in-app messaging system for initial communications:')}</p>
            <ul>
              {(() => {
                const list = t('beforeMeeting.communication.list', { returnObjects: true });
                const defaultList = [
                  'Keep conversations relevant to the ride or delivery',
                  'Be polite and professional in all interactions',
                  'Don\'t share personal information like home address immediately',
                  'Use the platform\'s messaging until you feel comfortable',
                  'Report any inappropriate messages immediately'
                ];
                const items = Array.isArray(list) ? list : defaultList;
                return items.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>

            <h3>{t('beforeMeeting.planning.title', '2.3 Planning Your Route')}</h3>
            <p>{t('beforeMeeting.planning.description', 'Always plan and share your route details:')}</p>
            <ul>
              {(() => {
                const list = t('beforeMeeting.planning.list', { returnObjects: true });
                const defaultList = [
                  'Use our route planning tools to calculate distances and times',
                  'Share your planned route with the other party',
                  'Agree on specific pickup and drop-off locations',
                  'Choose well-lit, public places for meetings when possible',
                  'Inform a trusted friend or family member about your plans'
                ];
                const items = Array.isArray(list) ? list : defaultList;
                return items.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>
          </section>

          <section id="meeting-safely">
            <h2>{t('meetingSafely.title', '3. Meeting Safely')}</h2>
            
            <h3>{t('meetingSafely.safeLocations.title', '3.1 Choose Safe Meeting Locations')}</h3>
            <div className="safety-tips-box">
              <h4>{t('meetingSafely.safeLocations.safePlaces.title', '✅ Safe Meeting Places:')}</h4>
              <ul>
                {(() => {
                  const list = t('meetingSafely.safeLocations.safePlaces.list', { returnObjects: true });
                  const defaultList = [
                    'Shopping centers and malls',
                    'Gas stations with good lighting',
                    'Public transportation hubs',
                    'Busy parking lots',
                    'Police stations (for high-value packages)',
                    '24-hour establishments'
                  ];
                  const items = Array.isArray(list) ? list : defaultList;
                  return items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ));
                })()}
              </ul>
            </div>

            <div className="safety-warning-box">
              <h4>{t('meetingSafely.safeLocations.avoidPlaces.title', '❌ Avoid These Locations:')}</h4>
              <ul>
                {(() => {
                  const list = t('meetingSafely.safeLocations.avoidPlaces.list', { returnObjects: true });
                  const defaultList = [
                    'Residential areas or private homes (initially)',
                    'Isolated or poorly lit areas',
                    'Empty parking lots',
                    'Remote or secluded locations',
                    'Private properties without good visibility'
                  ];
                  const items = Array.isArray(list) ? list : defaultList;
                  return items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ));
                })()}
              </ul>
            </div>

            <h3>{t('meetingSafely.meetingProtocol.title', '3.2 Meeting Protocol')}</h3>
            <p>{t('meetingSafely.meetingProtocol.description', 'Follow these steps when meeting another user:')}</p>
            <ol>
              {(() => {
                const list = t('meetingSafely.meetingProtocol.list', { returnObjects: true });
                const defaultList = [
                  'Arrive on time and confirm your arrival through the app',
                  'Verify identity by asking for their name and checking their profile photo',
                  'Confirm vehicle details (license plate, make, model, color)',
                  'Trust your instincts - if something doesn\'t feel right, leave',
                  'Stay in public view during the initial meeting',
                  'Keep your phone charged and accessible'
                ];
                const items = Array.isArray(list) ? list : defaultList;
                return items.map((item, index) => (
                  <li key={index}><strong>{item}</strong></li>
                ));
              })()}
            </ol>

            <h3>{t('meetingSafely.firstTime.title', '3.3 First-Time Meetings')}</h3>
            <p>{t('meetingSafely.firstTime.description', 'For first-time meetings with new users:')}</p>
            <ul>
              {(() => {
                const list = t('meetingSafely.firstTime.list', { returnObjects: true });
                const defaultList = [
                  'Meet during daylight hours when possible',
                  'Bring a friend if you feel uncomfortable',
                  'Start with shorter trips to build trust',
                  'Use video calls through the app if available',
                  'Take your time - don\'t rush into arrangements'
                ];
                const items = Array.isArray(list) ? list : defaultList;
                return items.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>
          </section>

          <section id="during-rides">
            <h2>{t('duringRides.title')}</h2>
            
            <h3>{t('duringRides.forPassengers.title')}</h3>
            <p>{t('duringRides.forPassengers.description')}</p>
            <ul>
              {(() => {
                const list = t('duringRides.forPassengers.list', { returnObjects: true });
                const items = Array.isArray(list) ? list : [];
                return items.map((item, index) => (
                  <li key={index}><strong>{item}</strong></li>
                ));
              })()}
            </ul>

            <h3>{t('duringRides.forDrivers.title')}</h3>
            <p>{t('duringRides.forDrivers.description')}</p>
            <ul>
              {(() => {
                const list = t('duringRides.forDrivers.list', { returnObjects: true });
                const items = Array.isArray(list) ? list : [];
                return items.map((item, index) => (
                  <li key={index}><strong>{item}</strong></li>
                ));
              })()}
            </ul>

            <h3>{t('duringRides.communication.title')}</h3>
            <p>{t('duringRides.communication.description')}</p>
            <ul>
              {(() => {
                const list = t('duringRides.communication.list', { returnObjects: true });
                const items = Array.isArray(list) ? list : [];
                return items.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>
          </section>

          <section id="package-safety">
            <h2>{t('packageSafety.title')}</h2>
            
            <h3>{t('packageSafety.guidelines.title', '5.1 Package Guidelines')}</h3>
            <div className="prohibited-items-box">
              <h4>{t('packageSafety.guidelines.prohibited.title', '🚫 Prohibited Items - Never Transport:')}</h4>
              <ul>
                {(() => {
                  const list = t('packageSafety.guidelines.prohibited.list', { returnObjects: true });
                  const defaultList = [
                    'Illegal substances or contraband',
                    'Weapons or dangerous items',
                    'Hazardous materials (flammable, toxic, explosive)',
                    'Live animals',
                    'Perishable food without proper packaging',
                    'Items requiring special permits',
                    'Anything that makes you uncomfortable'
                  ];
                  const items = Array.isArray(list) ? list : defaultList;
                  return items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ));
                })()}
              </ul>
            </div>

            <h3>{t('packageSafety.forSenders.title')}</h3>
            <p>{t('packageSafety.forSenders.description')}</p>
            <ul>
              {(() => {
                const list = t('packageSafety.forSenders.list', { returnObjects: true });
                const items = Array.isArray(list) ? list : [];
                return items.map((item, index) => (
                  <li key={index}><strong>{item}</strong></li>
                ));
              })()}
            </ul>

            <h3>{t('packageSafety.forCarriers.title')}</h3>
            <p>{t('packageSafety.forCarriers.description')}</p>
            <ul>
              {(() => {
                const list = t('packageSafety.forCarriers.list', { returnObjects: true });
                const items = Array.isArray(list) ? list : [];
                return items.map((item, index) => (
                  <li key={index}><strong>{item}</strong></li>
                ));
              })()}
            </ul>

            <h3>{t('packageSafety.highValue.title')}</h3>
            <p>{t('packageSafety.highValue.description')}</p>
            <ul>
              {(() => {
                const list = t('packageSafety.highValue.list', { returnObjects: true });
                const items = Array.isArray(list) ? list : [];
                return items.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>
          </section>

          <section id="personal-safety">
            <h2>{t('personalSafety.title')}</h2>
            
            <h3>{t('personalSafety.informationProtection.title')}</h3>
            <p>{t('personalSafety.informationProtection.description')}</p>
            <ul>
              {(() => {
                const list = t('personalSafety.informationProtection.list', { returnObjects: true });
                const items = Array.isArray(list) ? list : [];
                return items.map((item, index) => (
                  <li key={index}><strong>{item}</strong></li>
                ));
              })()}
            </ul>

            <h3>{t('personalSafety.trustBuilding.title')}</h3>
            <p>{t('personalSafety.trustBuilding.description')}</p>
            <ul>
              {(() => {
                const list = t('personalSafety.trustBuilding.list', { returnObjects: true });
                const items = Array.isArray(list) ? list : [];
                return items.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>

            <h3>{t('personalSafety.stayingAlert.title')}</h3>
            <p>{t('personalSafety.stayingAlert.description')}</p>
            <ul>
              {(() => {
                const list = t('personalSafety.stayingAlert.list', { returnObjects: true });
                const items = Array.isArray(list) ? list : [];
                return items.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>
          </section>

          <section id="vehicle-safety">
            <h2>{t('vehicleSafety.title')}</h2>
            
            <h3>{t('vehicleSafety.forOwners.title')}</h3>
            <p>{t('vehicleSafety.forOwners.description')}</p>
            <ul>
              {(() => {
                const list = t('vehicleSafety.forOwners.list', { returnObjects: true });
                const items = Array.isArray(list) ? list : [];
                return items.map((item, index) => (
                  <li key={index}><strong>{item}</strong></li>
                ));
              })()}
            </ul>

            <h3>{t('vehicleSafety.verification.title')}</h3>
            <p>{t('vehicleSafety.verification.description')}</p>
            <ul>
              {(() => {
                const list = t('vehicleSafety.verification.list', { returnObjects: true });
                const items = Array.isArray(list) ? list : [];
                return items.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>

            <h3>{t('vehicleSafety.inspection.title')}</h3>
            <p>{t('vehicleSafety.inspection.description')}</p>
            <ul>
              {(() => {
                const list = t('vehicleSafety.inspection.list', { returnObjects: true });
                const items = Array.isArray(list) ? list : [];
                return items.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>
          </section>

          <section id="communication">
            <h2>{t('communication.title')}</h2>
            
            <h3>{t('communication.inApp.title', '8.1 In-App Messaging')}</h3>
            <p>{t('communication.inApp.description', 'Use our secure messaging system:')}</p>
            <ul>
              {(() => {
                const list = t('communication.inApp.list', { returnObjects: true });
                const defaultList = [
                  'Keep initial conversations within the app',
                  'All messages are logged for safety purposes',
                  'Report inappropriate messages immediately',
                  'Don\'t share personal contact information initially',
                  'Use clear, professional language',
                  'Confirm details in writing before meeting'
                ];
                const items = Array.isArray(list) ? list : defaultList;
                return items.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>

            <h3>{t('communication.phone.title', '8.2 Phone Communication')}</h3>
            <p>{t('communication.phone.description', 'If you choose to share phone numbers:')}</p>
            <ul>
              {(() => {
                const list = t('communication.phone.list', { returnObjects: true });
                const defaultList = [
                  'Wait until you\'ve established trust through the app',
                  'Use for trip coordination only',
                  'Don\'t answer calls from unknown numbers',
                  'Keep conversations focused on the ride or delivery',
                  'Report harassment or inappropriate calls',
                  'Block numbers if needed'
                ];
                const items = Array.isArray(list) ? list : defaultList;
                return items.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>

            <h3>{t('communication.emergency.title', '8.3 Emergency Communication')}</h3>
            <p>{t('communication.emergency.description', 'Always keep emergency contacts accessible:')}</p>
            <ul>
              {(() => {
                const list = t('communication.emergency.list', { returnObjects: true });
                const defaultList = [
                  'Program emergency numbers into your phone',
                  'Share trip details with trusted contacts',
                  'Set up location sharing when possible',
                  'Know how to quickly contact local emergency services',
                  'Keep our safety hotline number accessible',
                  'Use emergency features on your phone if available'
                ];
                const items = Array.isArray(list) ? list : defaultList;
                return items.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>
          </section>

          <section id="red-flags">
            <h2>{t('redFlags.title', '9. Warning Signs and Red Flags')}</h2>
            
            <div className="warning-signs-box">
              <h4>{t('redFlags.immediateRedFlags.title', '🚨 Immediate Red Flags - Stop and Report:')}</h4>
              <ul>
                {(() => {
                  const list = t('redFlags.immediateRedFlags.list', { returnObjects: true });
                  const defaultList = [
                    'Requests to meet in isolated or inappropriate locations',
                    'Pressure to complete arrangements quickly without verification',
                    'Refusal to share vehicle information or verification details',
                    'Inappropriate sexual or threatening messages',
                    'Requests to transport suspicious or prohibited items',
                    'Offers that seem too good to be true',
                    'Users with no ratings or reviews',
                    'Vehicle details that don\'t match the profile',
                    'Aggressive or hostile behavior',
                    'Requests to avoid using the app\'s official features'
                  ];
                  const items = Array.isArray(list) ? list : defaultList;
                  return items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ));
                })()}
              </ul>
            </div>

            <h3>{t('redFlags.duringInteractions.title', '9.1 During Interactions')}</h3>
            <p>{t('redFlags.duringInteractions.description', 'Watch for these warning signs during meetings:')}</p>
            <ul>
              {(() => {
                const list = t('redFlags.duringInteractions.list', { returnObjects: true });
                const defaultList = [
                  'Person doesn\'t match their profile photo',
                  'Vehicle is significantly different from described',
                  'User seems impaired by substances',
                  'Inappropriate personal questions or comments',
                  'Pressure to change agreed-upon arrangements',
                  'Aggressive or threatening behavior',
                  'Requests to go to different locations than planned',
                  'Multiple people when you expected to meet one person'
                ];
                const items = Array.isArray(list) ? list : defaultList;
                return items.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>

            <h3>{t('redFlags.trustInstincts.title', '9.2 Trust Your Instincts')}</h3>
            <div className="instincts-box">
              <h4>{t('redFlags.trustInstincts.reminder.title', '🎯 Always Remember:')}</h4>
              <p>{t('redFlags.trustInstincts.reminder.description', 'If something feels wrong, it probably is. Your safety is more important than any ride or delivery. Don\'t hesitate to cancel arrangements if you feel uncomfortable.')}</p>
              <ul>
                {(() => {
                  const list = t('redFlags.trustInstincts.reminder.list', { returnObjects: true });
                  const defaultList = [
                    'You have the right to refuse any arrangement',
                    'No explanation is needed if you feel unsafe',
                    'There are always other rides and delivery options',
                    'Trust your gut feelings about people and situations',
                    'It\'s better to be cautious than sorry'
                  ];
                  const items = Array.isArray(list) ? list : defaultList;
                  return items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ));
                })()}
              </ul>
            </div>
          </section>

          <section id="emergency">
            <h2>{t('emergency.title', '10. Emergency Procedures')}</h2>
            
            <h3>{t('emergency.immediateEmergencies.title', '10.1 Immediate Emergencies')}</h3>
            <div className="emergency-box">
              <h4>{t('emergency.immediateEmergencies.inDanger.title', '🚨 In Case of Immediate Danger:')}</h4>
              <ol>
                {(() => {
                  const list = t('emergency.immediateEmergencies.inDanger.list', { returnObjects: true });
                  const defaultList = [
                    'Call local emergency services immediately (112 in Latvia)',
                    'Get to safety - leave the situation if possible',
                    'Contact police if criminal activity is suspected',
                    'Seek medical attention if injured',
                    'Report to BrauksimKopā as soon as you\'re safe'
                  ];
                  const items = Array.isArray(list) ? list : defaultList;
                  return items.map((item, index) => (
                    <li key={index}><strong>{item}</strong></li>
                  ));
                })()}
              </ol>
            </div>

            <h3>{t('emergency.emergencyContacts.title', '10.2 Emergency Contacts')}</h3>
            <div className="contact-info-box">
              {(() => {
                const list = t('emergency.emergencyContacts.list', { returnObjects: true });
                const defaultList = [
                  'Emergency Services (Latvia): 112',
                  'Police: 110',
                  'Medical Emergency: 113',
                  'BrauksimKopā Safety Hotline: safety@brauksimkopa.lv',
                  '24/7 Support: info@brauksimkopa.lv'
                ];
                const items = Array.isArray(list) ? list : defaultList;
                return items.map((item, index) => (
                  <p key={index}><strong>{item}</strong></p>
                ));
              })()}
            </div>

            <h3>{t('emergency.nonEmergency.title', '10.3 Non-Emergency Safety Issues')}</h3>
            <p>
              {t('emergency.nonEmergency.description', 'For safety concerns that are not immediate emergencies:')}
            </p>
            <ul>
              {(() => {
                const list = t('emergency.nonEmergency.list', { returnObjects: true });
                const defaultList = [
                  'Use our in-app reporting system',
                  'Email our safety team with detailed information',
                  'Document the incident with photos or screenshots if safe to do so',
                  'Block the user to prevent future contact',
                  'Consider reporting to local authorities if appropriate',
                  'Seek support from friends, family, or counseling services if needed'
                ];
                const items = Array.isArray(list) ? list : defaultList;
                return items.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>

            <h3>{t('emergency.afterIncident.title', '10.4 After an Incident')}</h3>
            <p>
              {t('emergency.afterIncident.description', 'Steps to take after a safety incident:')}
            </p>
            <ul>
              {(() => {
                const list = t('emergency.afterIncident.list', { returnObjects: true });
                const defaultList = [
                  'Ensure your immediate safety and get medical attention if needed',
                  'Report the incident to appropriate authorities',
                  'Contact BrauksimKopā safety team with full details',
                  'Preserve evidence (messages, photos, receipts)',
                  'Follow up with law enforcement if required',
                  'Consider counseling or support services if needed',
                  'Help us improve safety by sharing your experience'
                ];
                const items = Array.isArray(list) ? list : defaultList;
                return items.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>
          </section>

          <section id="reporting">
            <h2>{t('reporting.title', '11. Reporting Safety Issues')}</h2>
            
            <h3>{t('reporting.howToReport.title', '11.1 How to Report')}</h3>
            <p>
              {t('reporting.howToReport.description', 'Multiple ways to report safety concerns:')}
            </p>
            <ul>
              {(() => {
                const list = t('reporting.howToReport.list', { returnObjects: true });
                const defaultList = [
                  'In-App Reporting: Use the report button on user profiles or in messages',
                  'Email: safety@brauksimkopa.lv for detailed reports',
                  'Emergency Hotline: For urgent safety concerns',
                  'Bug Report System: For technical safety issues',
                  'Support Chat: Through our customer service system'
                ];
                const items = Array.isArray(list) ? list : defaultList;
                return items.map((item, index) => (
                  <li key={index}><strong>{item}</strong></li>
                ));
              })()}
            </ul>

            <h3>{t('reporting.whatToInclude.title', '11.2 What to Include in Reports')}</h3>
            <p>
              {t('reporting.whatToInclude.description', 'When reporting safety issues, include:')}
            </p>
            <ul>
              {(() => {
                const list = t('reporting.whatToInclude.list', { returnObjects: true });
                const defaultList = [
                  'Date, time, and location of the incident',
                  'Username and profile details of the other user',
                  'Detailed description of what happened',
                  'Screenshots of messages or profile information',
                  'Photos of vehicles or packages if relevant',
                  'Any witness information',
                  'Police report numbers if applicable'
                ];
                const items = Array.isArray(list) ? list : defaultList;
                return items.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>

            <h3>{t('reporting.ourResponse.title', '11.3 Our Response Process')}</h3>
            <p>
              {t('reporting.ourResponse.description', 'When you report a safety issue:')}
            </p>
            <ul>
              {(() => {
                const list = t('reporting.ourResponse.list', { returnObjects: true });
                const defaultList = [
                  'We acknowledge receipt within 24 hours',
                  'Urgent reports receive immediate attention',
                  'We investigate all reports thoroughly',
                  'Appropriate action is taken (warnings, suspensions, bans)',
                  'We follow up with you on the resolution',
                  'Serious incidents are reported to authorities when required',
                  'We use reports to improve our safety systems'
                ];
                const items = Array.isArray(list) ? list : defaultList;
                return items.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>
          </section>

          <section id="prevention">
            <h2>{t('prevention.title', '12. Prevention and Best Practices')}</h2>
            
            <h3>{t('prevention.dailyHabits.title', '12.1 Daily Safety Habits')}</h3>
            <p>
              {t('prevention.dailyHabits.description', 'Develop these safety habits:')}
            </p>
            <ul>
              {(() => {
                const list = t('prevention.dailyHabits.list', { returnObjects: true });
                const defaultList = [
                  'Always check user profiles and ratings before agreeing to arrangements',
                  'Share your trip plans with trusted friends or family',
                  'Keep your phone charged and location services enabled',
                  'Meet in public, well-lit locations whenever possible',
                  'Trust your instincts about people and situations',
                  'Stay alert and aware of your surroundings',
                  'Keep emergency contacts easily accessible'
                ];
                const items = Array.isArray(list) ? list : defaultList;
                return items.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>

            <h3>{t('prevention.buildingCommunity.title', '12.2 Building a Safe Community')}</h3>
            <p>
              {t('prevention.buildingCommunity.description', 'Help us create a safer platform:')}
            </p>
            <ul>
              {(() => {
                const list = t('prevention.buildingCommunity.list', { returnObjects: true });
                const defaultList = [
                  'Leave honest, helpful reviews for other users',
                  'Report suspicious behavior or safety violations',
                  'Be respectful and professional in all interactions',
                  'Complete your profile fully and verify your information',
                  'Follow all platform rules and guidelines',
                  'Share safety tips with other users when appropriate',
                  'Provide feedback to help improve our safety features'
                ];
                const items = Array.isArray(list) ? list : defaultList;
                return items.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>

            <h3>{t('prevention.continuousLearning.title', '12.3 Continuous Learning')}</h3>
            <p>
              {t('prevention.continuousLearning.description', 'Stay informed about safety:')}
            </p>
            <ul>
              {(() => {
                const list = t('prevention.continuousLearning.list', { returnObjects: true });
                const defaultList = [
                  'Read our safety updates and announcements',
                  'Learn about new safety features as we introduce them',
                  'Stay informed about local safety laws and regulations',
                  'Participate in safety education programs when available',
                  'Share your safety experiences to help others learn',
                  'Keep up with best practices for ridesharing and delivery safety'
                ];
                const items = Array.isArray(list) ? list : defaultList;
                return items.map((item, index) => (
                  <li key={index}>{item}</li>
                ));
              })()}
            </ul>

            <div className="final-reminder-box">
              <h4>{t('finalReminder.title', '🛡️ Final Safety Reminder')}</h4>
              <p>{t('finalReminder.description1', 'Your safety is our top priority, but it\'s also your responsibility. These guidelines are designed to help protect you, but they cannot guarantee your safety in every situation. Always use your best judgment, trust your instincts, and prioritize your wellbeing above all else.')}</p>
              <p>{t('finalReminder.description2', 'If you ever feel unsafe or encounter a safety issue, don\'t hesitate to contact emergency services first, then report the incident to us. Together, we can build a safer community for everyone on BrauksimKopā.')}</p>
            </div>
          </section>
        </div>
      </div>

      <div className="safety-guidelines-footer">
        <button onClick={() => navigate(-1)} className="safety-back-button">
          {t('buttons.goBack', 'Go Back')}
        </button>
        <button onClick={() => navigate('/')} className="safety-home-button">
          {t('buttons.homePage', 'Home Page')}
        </button>
      </div>
    </div>
  );
};

export default SafetyGuidelines;